/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useLayoutEffect } from "react";
import { IonApp, setupIonicReact, useIonLoading } from "@ionic/react";
import Subscriber from "./services/mq/Subscriber";
import { Provider } from "./state";
import Authorization from "./components/Authorization";
import Notifications from "./components/Notifications";
import AppRoutes from "./AppRoutes";
import * as jwtDecode from "jwt-decode";

import {
  Plugins,
  StatusBarStyle,
  PushNotificationToken,
  PushNotification,
  PushNotificationActionPerformed,
} from "@capacitor/core";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";
import AuthLoader from "./AuthLoader";
import PinEditorModal from "./components/PinEditorModal";
import PushTokenSyncLoading from "./components/PushTokenSyncLoading";
import PinFlow from "./pages/PinFlow";
import { setMobileIsAppLockedState } from "./utils/helpers/LocalStorage";
import AppConfig from "./AppConfig";
import MockDelay from "./utils/helpers/MockDelay";

setupIonicReact({
  mode: "ios",
});

const { Device, SplashScreen, PushNotifications, StatusBar } = Plugins;
interface stateITF {
  deviceId: any;
  wallet: any;
  pushToken: any;
}

const App: React.FC = () => {
  const [pendingShare, setPendingShare] = useState<any>(null);
  const [authorization, setAuthorization] = useState<any>(null);
  const [appConfig, setAppConfig] = useState<any>(null);
  const [present, dismiss] = useIonLoading()

  const [state, setState] = useState<stateITF>({
    deviceId: undefined,
    wallet: undefined,
    pushToken: undefined,
  });

  useLayoutEffect(() => {
    getAppConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // set lock state everytime the app loads, for checking
    setMobileIsAppLockedState(true);

    document.addEventListener("deviceready", async () => {
      StatusBar.setStyle({ style: StatusBarStyle.Dark });
      setTimeout(() => {
        SplashScreen.hide();
      }, 200);

      const device = await Device.getInfo();
      setState((prevState) => ({ ...prevState, deviceId: device.uuid }));

      const setAlerts = (notificationPayload: any) => {
        const {
          data: { appId, ui, token, requestId, requestAction, authRequestId },
          sub,
          iss,
        } = notificationPayload;

        if (iss === "auth") {
          let header = "Sign In Authorization";
          let message = `You have a Sign In request for ${ui}`;

          if (requestAction && requestAction.indexOf("api-key") !== -1) {
            header = "Action Authorization";
            message = `You have an API key ${requestAction.slice(
              8,
              requestAction.length
            )} request for ${ui}`;
          }

          setAuthorization({
            header,
            message,
            identity: sub,
            token,
            appId,
            requestId,
            authRequestId,
          });
        }

        if (iss === "share") {
          const { aud, keyid } = jwtDecode(token);
          setPendingShare({
            sub,
            aud,
            keyid,
            iss,
            token,
          });
        }
      };

      PushNotifications.requestPermission().then((result) => {
        if (result.granted) {
          PushNotifications.register();
        } else {
          // todo show some error with AlertBox
        }
      });

      // On success, we should be able to receive notifications
      PushNotifications.addListener(
        "registration",
        (token: PushNotificationToken) => {
          // console.log("push token:", token);

          if (token && token.value) {
            setState((prevState) => ({ ...prevState, pushToken: token.value }));
          }
        }
      );

      // Some issue with our setup and push will not work
      PushNotifications.addListener("registrationError", (error: any) => {
        // todo show some error with AlertBox
        // console.log("Error on registration: " + JSON.stringify(error));
      });

      // Show us the notification payload if the app is open on our device
      PushNotifications.addListener(
        "pushNotificationReceived",
        (notification: PushNotification) => {
          // console.log("Push received:", JSON.stringify(notification));
          // console.log("Push received:", notification);

          const notificationPayload: any = jwtDecode(notification.data.token);
          setAlerts(notificationPayload);
        }
      );

      // Method called when tapping on a notification
      PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (notification: PushNotificationActionPerformed) => {
          const notificationPayload = jwtDecode(
            notification.notification.data.token
          );
          setAlerts(notificationPayload);

          // todo parse for android push
          // const alertTitle = data.aps ? data.aps.alert.title : data.gcm.title
          // const alertBody = data.aps ? data.aps.alert.body : data.gcm.body
          // const alertData = data.aps ? JSON.parse(data['gcm.notification.data']).token : data.token
        }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAppConfig = async () => {
    try {
      await present({ message: 'Initializing...'})
      await MockDelay(5000)
      const resp: any = await window.idDwn.dwn.metadata.get(process.env.REACT_APP_CONFIG);
      if (resp) {
        setAppConfig(JSON.parse(resp))
      }
      await dismiss()
    } catch (e) {
      console.log(e)
    }
  }

  return (
    // @ts-ignore
    <Provider>
      <IonApp>
        <AppRoutes />
        {appConfig && <AppConfig config={appConfig} />}
        <Subscriber
          deviceId={state.deviceId}
          pushToken={state.pushToken}
          pendingShare={pendingShare}
          setPendingShare={setPendingShare}
        />
        <Authorization
          authorization={authorization}
          setAuthrization={setAuthorization}
        />
        <Notifications />
        <AuthLoader />
        <PinEditorModal />
        <PushTokenSyncLoading />
        <PinFlow />
      </IonApp>
    </Provider>
  );
};

export default App;
